import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiError } from '../../admin/api/apiError'
import { useDealerToast } from '../components/Toast/DealerToast'
import { markOrderSent, trackOrderEvent, unmarkOrderSent, updateOrderClientPhone } from '../services/orderService'
import { copyText } from '../utils/clipboard'
import type { DealerOrderItem } from '../types'

export type OrderPatch = (id: string, patch: Partial<DealerOrderItem>) => void

export interface OrderActions {
  copyPhone: (item: DealerOrderItem) => void
  copyLink: (item: DealerOrderItem) => void
  markSent: (item: DealerOrderItem) => void
  undoSent: (item: DealerOrderItem) => void
  // null — сохранено; иначе текст ошибки для показа у поля
  savePhone: (item: DealerOrderItem, phone: string) => Promise<string | null>
  isBusy: (id: string) => boolean
  // отмечен отправленным в этой сессии экрана — показываем «Отправлено ✓» с «Отменить»
  isJustSent: (id: string) => boolean
}

// Общие действия над заказом для списка заявок, дашборда и боковой панели.
// onPatch применяет изменения к локальному списку на экране (без перезагрузки).
export function useOrderActions(onPatch: OrderPatch): OrderActions {
  const { t } = useTranslation()
  const { showToast } = useDealerToast()
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set())
  const [justSentIds, setJustSentIds] = useState<ReadonlySet<string>>(new Set())

  const setBusy = useCallback((id: string, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev)
      if (busy) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const setJustSent = useCallback((id: string, value: boolean) => {
    setJustSentIds((prev) => {
      const next = new Set(prev)
      if (value) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const copyPhone = useCallback(async (item: DealerOrderItem) => {
    if (!item.clientPhone) return
    const ok = await copyText(item.clientPhone)
    if (!ok) {
      showToast(t('dealer.orders.copy_failed'), 'error')
      return
    }
    showToast(t('dealer.orders.phone_copied'))
    onPatch(item.id, { phoneCopiedAt: new Date().toISOString() })
    trackOrderEvent(item.id, 'PHONE_COPIED')
  }, [onPatch, showToast, t])

  // копируем чистую ссылку — именно её получает клиент, без preview
  const copyLink = useCallback(async (item: DealerOrderItem) => {
    if (!item.reportUrl) return
    const ok = await copyText(item.reportUrl)
    if (!ok) {
      showToast(t('dealer.orders.copy_failed'), 'error')
      return
    }
    showToast(t('dealer.orders.link_copied'))
    onPatch(item.id, { linkCopiedAt: new Date().toISOString() })
    trackOrderEvent(item.id, 'LINK_COPIED')
  }, [onPatch, showToast, t])

  const markSent = useCallback(async (item: DealerOrderItem) => {
    setBusy(item.id, true)
    try {
      const updated = await markOrderSent(item.id)
      onPatch(item.id, updated)
      setJustSent(item.id, true)
      showToast(t('dealer.orders.marked_sent', { number: item.orderNumber }))
    } catch (err) {
      const { status, code } = apiError(err)
      if (code === 'REPORT_NOT_READY') showToast(t('dealer.orders.error_not_ready'), 'error')
      else if (!status) showToast(t('dealer.common.error_network'), 'error')
      else showToast(t('dealer.orders.error_mark_sent'), 'error')
      console.error(`[ERROR] Failed to mark order as sent — id: ${item.id}`, err)
    } finally {
      setBusy(item.id, false)
    }
  }, [onPatch, setBusy, setJustSent, showToast, t])

  const undoSent = useCallback(async (item: DealerOrderItem) => {
    setBusy(item.id, true)
    try {
      const updated = await unmarkOrderSent(item.id)
      onPatch(item.id, updated)
      setJustSent(item.id, false)
      showToast(t('dealer.orders.unmarked_sent', { number: item.orderNumber }), 'info')
    } catch (err) {
      const { status, code } = apiError(err)
      if (code === 'REPORT_NOT_READY') showToast(t('dealer.orders.error_not_ready'), 'error')
      else showToast(status ? t('dealer.orders.error_undo') : t('dealer.common.error_network'), 'error')
      console.error(`[ERROR] Failed to unmark order as sent — id: ${item.id}`, err)
    } finally {
      setBusy(item.id, false)
    }
  }, [onPatch, setBusy, setJustSent, showToast, t])

  const savePhone = useCallback(async (item: DealerOrderItem, phone: string): Promise<string | null> => {
    setBusy(item.id, true)
    try {
      const updated = await updateOrderClientPhone(item.id, phone)
      onPatch(item.id, updated)
      showToast(updated.clientPhone ? t('dealer.orders.phone_saved') : t('dealer.orders.phone_removed'))
      return null
    } catch (err) {
      const { status, code } = apiError(err)
      console.error(`[ERROR] Failed to update client phone — id: ${item.id}`, err)
      if (code === 'PHONE_INVALID') return t('dealer.orders.error_phone_invalid')
      return status ? t('dealer.orders.error_phone_save') : t('dealer.common.error_network')
    } finally {
      setBusy(item.id, false)
    }
  }, [onPatch, setBusy, showToast, t])

  return {
    copyPhone,
    savePhone,
    copyLink,
    markSent,
    undoSent,
    isBusy: (id) => busyIds.has(id),
    isJustSent: (id) => justSentIds.has(id),
  }
}
